import { useEffect, useRef } from "react";
import { store, useAppDispatch, useAppSelector } from "store/store";
import { getDatabase, onValue, ref } from "firebase/database";
import { AlertName, AlertType } from "helpers/alert-type";
import { setIsPrimaryDisconnected } from "store/app.slice";
import { waitTimeForRecordingStart } from "../config/constant";
import { alertHelper } from "helpers/alert-helper";
import { participantGroupSessionPath } from "helpers/firebase-path";
import { firebaseCollections, SessionScope } from "globals/enums";

interface Alert {
  alert_type_id: number;
  timestamp?: string;
  updated_at: string;
  description?: string;
}

const faceVerificationAlertIds = [
  AlertType[AlertName.FaceVerificationCheckSuccess].id,
  AlertType[AlertName.FaceVerificationCheckFailed].id
];

const useWatchAlerts = () => {
  const session = useAppSelector((state) => state.app.session);
  const participantGroupId = useAppSelector((state) => state.app.channelData.participant_group_id);
  const alertConfig = useAppSelector((state) => state.app.alertConfigs);
  const dispatch = useAppDispatch();
  const dispatchIsPrimaryDisconnectedTimeout = useRef<number>();
  const processedFaceVerificationAlerts = useRef<Set<string>>(new Set());
  const alertConfigRef = useRef(alertConfig);

  useEffect(() => {
    alertConfigRef.current = alertConfig;
  }, [alertConfig]);

  const isPrimaryDisconnected = (alerts: Record<string, Alert>, timeStamp: string) => {
    const alertsArray: Alert[] = Object.values(alerts);

    const referenceAlert: Alert = alertsArray.find(
      (alert) =>
        alert.alert_type_id === AlertType[AlertName.RecordingStarted].id &&
        alert.timestamp === timeStamp
    );

    if (!referenceAlert) return false;

    const referenceTime = new Date(referenceAlert.updated_at);

    return alertsArray.some(
      (alert) =>
        alert.alert_type_id === AlertType[AlertName.PrimaryCameradisconnected].id &&
        new Date(alert.updated_at) > referenceTime
    );
  };

  const primaryRecordingStoppedCheck = (alerts: Record<string, Alert>, timeStamp: string) => {
    const alertsArray: Alert[] = Object.values(alerts);

    const recordingStartedAlert = alertsArray.find(
      (alert) =>
        alert.alert_type_id === AlertType[AlertName.RecordingStarted].id &&
        alert.timestamp === timeStamp
    );
    if (!recordingStartedAlert) return;

    let referenceAlert: Alert | undefined;

    if (recordingStartedAlert) {
      const primaryCameraRecordingStoppedId = AlertType[AlertName.PrimaryCamerarecordingstopped].id;

      const stoppedAfterRecording = alertsArray
        .filter(
          (alert) =>
            alert.alert_type_id === primaryCameraRecordingStoppedId &&
            new Date(alert.updated_at) > new Date(recordingStartedAlert.updated_at)
        )
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];

      referenceAlert = stoppedAfterRecording || recordingStartedAlert;
    }

    const newRecordingStarted = alertsArray.find(
      (alert) =>
        alert.alert_type_id === AlertType[AlertName.PrimaryCamerarecordingstarted].id &&
        new Date(alert.updated_at) > new Date(referenceAlert.updated_at)!
    );

    if (newRecordingStarted) {
      if (dispatchIsPrimaryDisconnectedTimeout.current) {
        clearTimeout(dispatchIsPrimaryDisconnectedTimeout.current);
        dispatchIsPrimaryDisconnectedTimeout.current = undefined;
      }
      return;
    }

    if (!dispatchIsPrimaryDisconnectedTimeout.current) {
      dispatchIsPrimaryDisconnectedTimeout.current = window.setTimeout(() => {
        dispatch(setIsPrimaryDisconnected({ isPrimaryDisconnected: true }));
        dispatchIsPrimaryDisconnectedTimeout.current = undefined;
      }, waitTimeForRecordingStart);
    }

    return;
  };

  useEffect(() => {
    if (!session.session_id) return;
    if (
      session.session_type === SessionScope.LiveProctor &&
      !(typeof participantGroupId === "number" && participantGroupId > 0)
    ) {
      return;
    }

    const alertsRef = ref(
      getDatabase(),
      session.session_type === SessionScope.LiveProctor
        ? participantGroupSessionPath(participantGroupId, session.session_id, "alerts")
        : `${firebaseCollections.SessionV2}/${session.session_id}/alerts`
    );
    const raiseFaceVerificationAlerts = (alerts: Record<string, Alert>) => {
      Object.entries(alerts).forEach(([key, alert]) => {
        if (!alert || processedFaceVerificationAlerts.current.has(key)) {
          return;
        }

        if (!faceVerificationAlertIds.includes(alert.alert_type_id)) {
          return;
        }

        processedFaceVerificationAlerts.current.add(key);

        const alertName =
          alert.alert_type_id === AlertType[AlertName.FaceVerificationCheckSuccess].id
            ? AlertName.FaceVerificationCheckSuccess
            : AlertName.FaceVerificationCheckFailed;

        alertHelper(alertConfigRef.current || []).raiseAlert(
          alertName,
          dispatch,
          alert.description,
          null,
          true
        );
      });
    };

    const unsubscribe = onValue(alertsRef, (sessionSnapShot) => {
      const alertList = sessionSnapShot.val() || {};
      const primaryDisconnected = isPrimaryDisconnected(
        alertList,
        store.getState().app.recordingStartedTimestamp
      );
      dispatch(setIsPrimaryDisconnected({ isPrimaryDisconnected: primaryDisconnected }));
      primaryRecordingStoppedCheck(alertList, store.getState().app.recordingStartedTimestamp);
      raiseFaceVerificationAlerts(alertList);
    });

    return () => {
      unsubscribe();
      processedFaceVerificationAlerts.current.clear();
    };
  }, [session.session_id, session.session_type, participantGroupId, dispatch]);

  return null;
};

export default useWatchAlerts;
